import React, { useState, useEffect } from "react";
import JsonView from "./JsonView";
import ToolsFormView from "./ToolsFormView";
import PolicyFormView from "./PolicyFormView";
import ScopesFormView from "./ScopesFormView";
import { useFileChangeDetection } from "../../../hooks/useFileChangeDetection";

/**
 * DualViewEditor - Provides form and JSON views for configuration files
 * 
 * Supported file types:
 * - tools: {"tools": [...]} for expert agents OR {"agents": [...]} for swarm agents
 * - policy: Complex nested object with qz_pol and initial_data
 * - scopes: {"scopes": [...]} OAuth scope arrays
 * - prompt: Plain text (no form view, JSON view only)
 * 
 * Data Flow:
 * - Parent passes value as JSON string or plain text
 * - Component parses and maintains internal state
 * - onChange called with stringified value on any change
 * - onValidationError called when validation state changes
 * 
 * State Management:
 * - mode: Which view is active (form/json)
 * - Internal parsing state managed per view
 * - Single source of truth: value prop (parent state)
 * 
 * @param {Object} props
 * @param {'tools'|'policy'|'scopes'|'prompt'} props.fileType - Type of file being edited
 * @param {string} props.value - JSON string or plain text content
 * @param {Function} props.onChange - Callback(newValue: string) when content changes
 * @param {Function} props.onValidationError - Callback(error: string|null) on validation change
 * @param {'form'|'json'} props.initialMode - Initial view mode (default: 'form')
 * @param {Array<{agent_id: string, name: string, description: string}>} props.availableAgents - For swarm agent selection
 * @param {boolean} props.readOnly - Disable editing (default: false)
 * 
 * Validation Rules:
 * - tools.json: Must have "tools" OR "agents" key (not both), non-empty string arrays
 * - policy.json: Must have qz_pol.mcp_config structure
 * - scopes.json: Must have "scopes" array
 * - prompt.txt: No validation (plain text)
 * - JSON files: Must be valid JSON syntax
 * 
 * Edge Cases Handled:
 * - Empty value prop -> use default template
 * - Invalid JSON -> stay in JSON view, show error
 * - Switching agent types -> warn about data loss
 * - Missing availableAgents -> disable swarm selection
 */
const DualViewEditor = ({
  fileType = "tools",
  value = "",
  initialValue = "",
  onChange,
  onValidationError,
  onChangeDetected,
  initialMode = "form",
  availableAgents = [],
  currentAgentId = null,
  readOnly = false,
  policyAtom = null,
  connectionManagedExternally = false,
}) => {
  // Prompt files only support JSON view (plain text, no structured form)
  const supportsFormView = fileType === "tools" || fileType === "policy" || fileType === "scopes";
  const effectiveMode = supportsFormView ? initialMode : "json";
  
  const [mode, setMode] = useState(effectiveMode);
  // eslint-disable-next-line no-unused-vars
  const [validationError, setValidationError] = useState(null);
  
  // Lifted state for tools form to preserve across view switches
  const [formState, setFormState] = useState(null);

  // Initialize form state from value on first mount
  useEffect(() => {
    if (fileType === "tools" && value && !formState) {
      try {
        const parsed = JSON.parse(value);
        setFormState({
          expertTools: parsed.tools || [],
          swarmAgents: parsed.agents || [],
          agentType: parsed.tools !== undefined ? "expert" : "swarm"
        });
      } catch {
        // Invalid JSON, use defaults
        setFormState({
          expertTools: [],
          swarmAgents: [],
          agentType: "expert"
        });
      }
    } else if (fileType === "scopes" && value && !formState) {
      try {
        const parsed = JSON.parse(value);
        setFormState({
          scopes: parsed.scopes || []
        });
      } catch {
        // Invalid JSON, use defaults
        setFormState({
          scopes: []
        });
      }
    }
  }, [value, formState, fileType]);

  // Change detection
  const changeDetection = useFileChangeDetection(value, initialValue, fileType);

  // Notify parent about changes
  useEffect(() => {
    if (onChangeDetected) {
      onChangeDetected(changeDetection);
    }
  }, [changeDetection, onChangeDetected]);

  // Handle tab switching - allow free switching, validation shows inline
  const handleModeSwitch = (newMode) => {
    setMode(newMode);
  };

  // Handle validation errors from child components
  const handleValidationError = (error) => {
    setValidationError(error);
    if (onValidationError) {
      onValidationError(error);
    }
  };

  return (
    <div className="w-full">
      {/* Content */}
      {fileType === "tools" ? (
        <ToolsFormView
          value={value}
          onChange={onChange}
          availableAgents={availableAgents}
          currentAgentId={currentAgentId}
          readOnly={readOnly}
          formState={formState}
          onFormStateChange={setFormState}
          mode={mode}
          onModeSwitch={handleModeSwitch}
          supportsFormView={supportsFormView}
          onError={handleValidationError}
          fileType={fileType}
        />
      ) : fileType === "policy" ? (
        <PolicyFormView
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          mode={mode}
          onModeSwitch={handleModeSwitch}
          supportsFormView={supportsFormView}
          onError={handleValidationError}
          fileType={fileType}
          policyAtom={policyAtom}
          connectionManagedExternally={connectionManagedExternally}
        />
      ) : fileType === "scopes" ? (
        <ScopesFormView
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          formState={formState}
          onFormStateChange={setFormState}
          mode={mode}
          onModeSwitch={handleModeSwitch}
          supportsFormView={supportsFormView}
          onError={handleValidationError}
          fileType={fileType}
        />
      ) : mode === "json" ? (
        <JsonView
          value={value}
          onChange={onChange}
          onError={handleValidationError}
          fileType={fileType}
          readOnly={readOnly}
        />
      ) : null}
    </div>
  );
};

export default DualViewEditor;
